import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ViewInAr as ViewIn3dIcon } from '@mui/icons-material';
import {
  Typography,
  InfoItem,
  Button,
  FileRow,
  ImageGallery,
  ImageGalleryCard,
  BorderLine,
  EmptyFilesMessage,
  enqueueSnackbar,
  fullScreenButtonClick,
  HideInput,
  FileInput,
  SimpleTable,
  InfoItemTooltip,
  LoadingIcon,
  yardPlaceSceneViewModal,
} from '../../design-system';
import {
  DashboardFormWrapper,
  DashboardHeader,
  DashboardFormCards,
  DashboardFormThreeCards,
  DashboardFormCard,
  DashboardFormCustomColumn,
  DashboardFooterWithResize,
  CloseIconButton,
} from '../dashboard';
import { useStore } from '../../context';
import {
  useStatesWithFilterCounter,
  useUpdateImagesMutation,
  useUpdateDocumentsMutation,
  useIsSmallScreen,
  smallScreenSize,
  useYardBlocksSceneQuery,
} from '../../hooks';
import {
  showToRoles,
  momentTextFormat,
  filesFormatValidator,
  getFilesChanges,
  formatPhoneNumber,
  formatContainerType,
  formatContainerPlace,
  checkContainerPlace,
} from '../../utils';
import { containersService } from '../../services';
import {
  containerConditionsLabel,
  containerHistoriesComment,
  containerStatusLabel,
  INPUT_ERRORS,
  MESSAGES,
} from '../../const';
import { ContainersPage } from './ContainersComponents';
import { i18n } from '../../i18n';
import { ContainerGetOneQuery } from '../../types';
import { TableCell } from '@mui/material';

export const ContainersViewPage = styled(ContainersPage)`
  grid-template-rows: initial;
  grid-auto-rows: max-content;

  @media (min-width: ${smallScreenSize + 1}px) {
    & > * {
      padding-bottom: 0;
    }
  }

  & > *:last-child {
    padding-bottom: var(--spacing-10);

    @media (max-width: ${smallScreenSize}px) {
      padding-bottom: 0;
    }
  }

  @media (max-width: ${smallScreenSize}px) {
    overflow-y: auto;
    padding-right: var(--spacing-6);
    margin-right: calc(-1 * var(--spacing-6));
  }
`;

const ContainersViewHistoriesWrapper = styled.div`
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-row-gap: var(--spacing-6);
`;

export const ContainersViewHeader = ({ container }: { container: ContainerGetOneQuery }) => {
  const navigate = useNavigate();
  const isSmallScreen = useIsSmallScreen();

  const { data: blocksScene, isFetching } = useYardBlocksSceneQuery([container.id], {
    containerId: container.id,
  });

  const handleViewIn3dClick = () => {
    if (isFetching) return;

    yardPlaceSceneViewModal.blocksScene = blocksScene;
    yardPlaceSceneViewModal.defaultSelected = container.number;
    yardPlaceSceneViewModal.open();
  };

  return (
    <DashboardHeader isSmallScreen={isSmallScreen}>
      <Typography variant={isSmallScreen ? 'textL' : 'textXL'} weight={'bold'}>
        {!isSmallScreen
          ? container.number
          : `${container.number}: ${formatContainerType(container.type)}`}
      </Typography>
      {checkContainerPlace(container) && (
        <Button
          className={'row-button-square'}
          size={'medium'}
          data-color={'primary'}
          isSquare={true}
          onClick={handleViewIn3dClick}
          disabled={!blocksScene || isFetching}
        >
          {!isFetching && <ViewIn3dIcon />}
          {isFetching && <LoadingIcon color={'--white'} />}
        </Button>
      )}
      {!isSmallScreen && (
        <Button size={'medium'} data-color={'white'} to={-1}>
          {i18n.t('button.goBack')}
        </Button>
      )}
      {isSmallScreen && <CloseIconButton onClick={() => navigate(-1)} />}
    </DashboardHeader>
  );
};

export const ContainerFormCards = ({ children }: any) => {
  return (
    <DashboardFormWrapper flow={'row'}>
      <DashboardFormCards id={'containers-view'}>{children}</DashboardFormCards>
    </DashboardFormWrapper>
  );
};

export const ContainersViewTopCards = ({ container }: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  return (
    <DashboardFormThreeCards>
      <DashboardFormCard paperSize={'full-size'}>
        <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
          {i18n.t('common.generalInformation')}
        </Typography>
        <InfoItem label={i18n.t('label.containerNumber')} value={container.number} />
        <InfoItem
          label={i18n.t('label.containerType')}
          value={formatContainerType(container.type)}
        />
        <InfoItem
          label={i18n.t('label.containerCondition')}
          value={
            container.condition &&
            containerConditionsLabel[container.condition as keyof typeof containerConditionsLabel]
          }
        />
        <InfoItem
          label={i18n.t('label.containerStatus')}
          value={containerStatusLabel[container.status]}
        />
        <InfoItem label={i18n.t('label.place')} value={formatContainerPlace(container)} />
        {!!container.isHold && (
          <InfoItem label={i18n.t('label.holdBy')} value={container.holdByName} />
        )}
        <InfoItem label={i18n.t('label.comment')} value={container.comment} breakSpaces />
      </DashboardFormCard>
      <DashboardFormCard paperSize={'full-size'}>
        <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
          {i18n.t('pages.containerView.gateIn')}
        </Typography>
        <InfoItem
          label={i18n.t('label.gateInDate')}
          value={
            container.gateIn.completedAt && momentTextFormat(container.gateIn.completedAt, user)
          }
        />
        <InfoItem
          label={i18n.t('label.driverName')}
          value={[container.gateIn?.driver?.firstName, container.gateIn?.driver?.lastName]}
        />
        <InfoItem
          label={i18n.t('label.driverPhoneNumber')}
          value={formatPhoneNumber(container.gateIn?.driver?.phoneNumber)}
        />
        <InfoItem
          label={
            container.gateIn?.driver?.licensePlateNumber === 'TRANSFERRED' ||
            container.gateIn?.driver?.licensePlateNumber === 'MARKET'
              ? i18n.t('label.gateInType')
              : i18n.t('label.licensePlateNumber')
          }
          value={container.gateIn?.driver?.licensePlateNumber}
        />
        {showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_MANAGER'], user) && (
          <InfoItem label={i18n.t('label.gatedInBy')} value={container.gatedInByName} />
        )}
        {container.gateIn?.transferredFrom && (
          <InfoItem label={i18n.t('label.gateInCode')} value={container.gateIn?.transferredFrom} />
        )}
      </DashboardFormCard>
      <DashboardFormCard paperSize={'full-size'}>
        <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
          {i18n.t('pages.containerView.gateOut')}
        </Typography>
        <InfoItem
          label={i18n.t('label.gateOutDate')}
          value={
            container.gateOut?.completedAt && momentTextFormat(container.gateOut?.completedAt, user)
          }
        />
        <InfoItem
          label={i18n.t('label.driverName')}
          value={[container.gateOut?.driver?.firstName, container.gateOut?.driver?.lastName]}
        />
        <InfoItem
          label={i18n.t('label.driverPhoneNumber')}
          value={formatPhoneNumber(container.gateOut?.driver?.phoneNumber)}
        />
        <InfoItem
          label={
            container.gateOut?.driver?.licensePlateNumber === 'TRANSFERRED' ||
            container.gateOut?.driver?.licensePlateNumber === 'MARKET'
              ? i18n.t('label.gateOutType')
              : i18n.t('label.licensePlateNumber')
          }
          value={container.gateOut?.driver?.licensePlateNumber}
        />
        {showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_MANAGER'], user) && (
          <InfoItem label={i18n.t('label.gatedOutBy')} value={container.gatedOutByName} />
        )}
        {container.gateOut?.transferredTo && (
          <InfoItem label={i18n.t('label.gateOutCode')} value={container.gateOut?.transferredTo} />
        )}
        <InfoItem label={i18n.t('label.loadedBy')} value={container.loadedByName} />
        {showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_MANAGER'], user) && (
          <InfoItemTooltip
            label={i18n.t('label.comment')}
            value={container.gateOut?.comment}
            breakSpaces
          />
        )}
      </DashboardFormCard>
    </DashboardFormThreeCards>
  );
};

export const ContainersViewImages = ({ container }: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const updateImagesMutation = useUpdateImagesMutation();
  const isSmallScreen = useIsSmallScreen();

  const [photos, setPhotos] = useState<any>([]);
  const photosFiles = useRef<any>([]);
  const [oldPhotos, setOldPhotos] = useState<any>(container.images || []);
  const deletePhotos = useRef<any>([]);
  const photosInputRef = useRef<any>();
  const [zoomImageIndex, setZoomImageIndex] = useState(0);
  const [galleryImages, setGalleryImages] = useState<any>([]);
  const [, setStates, filteredStates] = useStatesWithFilterCounter(
    {
      photosLength: 0,
      isDeletePhotos: false,
    },
    {},
    0,
    false,
  );

  const handleChangePhotos = (newImages: any, files: any) => {
    const newPhotosValues = [...photos, ...newImages];
    if (filesFormatValidator(files, 'Image')) {
      enqueueSnackbar(filesFormatValidator(files, 'Image'), 'warning');
      return;
    }

    setPhotos(newPhotosValues);
    photosFiles.current = [...photosFiles.current, ...files];
    setStates('photosLength', newPhotosValues.length);
    photosInputRef.current.type = 'text';
    photosInputRef.current.type = 'file';
  };

  const handleZoomPhotoClick = (i: number) => {
    setGalleryImages([...oldPhotos.map(({ url }: any) => url), ...photos]);
    setZoomImageIndex(i);
    fullScreenButtonClick();
  };

  const handleDownloadImagesClick = () => {
    containersService.downloadImages(oldPhotos, container.number);
  };

  const handleDeletePhotoClick = (i: number) => {
    if (i - oldPhotos.length >= 0) {
      i = i - oldPhotos.length;
      photosFiles.current = photosFiles.current.filter((_: any, index: number) => index !== i);

      setPhotos(photos.filter((_: any, index: number) => index !== i));
      setStates('photosLength', photos.length - 1);
    } else {
      deletePhotos.current = [
        ...deletePhotos.current,
        { action: 'delete', name: oldPhotos[i].name },
      ];

      setOldPhotos(oldPhotos.filter((_: any, index: number) => index !== i));
      setStates('isDeletePhotos', true);
    }
  };

  const handleCancelClick = () => {
    photosFiles.current = [];
    deletePhotos.current = [];
    setPhotos([]);
    setOldPhotos(container.images || []);
    setStates('force', {
      photosLength: 0,
      isDeletePhotos: false,
    });
  };

  const handleSubmitClick = async () => {
    const data: any = {};

    if (photos.length > 10) {
      enqueueSnackbar(INPUT_ERRORS.NOT_VALID.MAX_UPLOAD_FILES, 'warning');
      return;
    }
    data.photos = getFilesChanges(photos, photosFiles.current, deletePhotos.current);

    if (!data.photos.length) return;
    if (!container.id) return;

    await updateImagesMutation.mutate(
      { id: container.id, photos: data.photos },
      {
        onSuccess: (res) => {
          if (res) {
            enqueueSnackbar(MESSAGES.CONTAINERS.SUCCESS.FILES_CHANGE, 'success');
            container.images = [
              ...oldPhotos,
              ...photos.map((file: any, i: string) => ({
                description: '',
                name: res[i].name || photosFiles.current[i].name,
                url: res[i].url || file,
              })),
            ];
            photosFiles.current = [];
            setOldPhotos(container.images);
            setPhotos([]);
            setStates('force', {
              photosLength: 0,
              isDeletePhotos: false,
            });
          } else enqueueSnackbar(MESSAGES.ERROR_BOUNDARY.DEFAULT, 'error');
        },
      },
    );
  };

  return (
    <>
      <ImageGallery photos={galleryImages} startIndex={zoomImageIndex} />
      {isSmallScreen && (
        <Typography variant={'textM'} weight={'semibold'} color={'--gray-90'}>
          {i18n.t('common.images')}
        </Typography>
      )}
      <DashboardFormCard
        paperSize={isSmallScreen ? 'none' : 'full-size'}
        rows={'max-content max-content 1fr'}
      >
        {!isSmallScreen && (
          <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
            {i18n.t('common.images')}
          </Typography>
        )}
        {!isSmallScreen && <BorderLine space={'dashboard-form-card'} />}
        <DashboardFormCustomColumn
          columns={isSmallScreen ? '1fr 1fr' : '1fr 1fr 1fr'}
          alignContent={!oldPhotos.length && !photos.length ? 'center' : 'initial'}
        >
          {!oldPhotos.length && !photos.length && (
            <EmptyFilesMessage variant={'textM'} align={'center'} gridColumn={'1 / 4'}>
              {i18n.t('pages.containerView.noImages')}
            </EmptyFilesMessage>
          )}
          {[...oldPhotos, ...photos].map((img: any, i: any) => {
            let isHideDeleteButton = showToRoles(['CLIENT_MANAGER', 'CLIENT_DISPATCHER'], user);
            if (typeof img === 'object') {
              isHideDeleteButton = isHideDeleteButton || !img.name;
              img = img.url;
            }
            if (
              oldPhotos.length > i &&
              !showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_MANAGER'], user)
            ) {
              isHideDeleteButton = true;
            }
            if ([...oldPhotos, ...photos].length <= 1) {
              isHideDeleteButton = true;
            }
            return (
              <ImageGalleryCard
                key={i}
                img={img}
                handleZoomClick={() => handleZoomPhotoClick(i)}
                handleDeleteImageClick={() => handleDeletePhotoClick(i)}
                hideDeleteButton={isHideDeleteButton}
              />
            );
          })}
          <HideInput>
            {showToRoles('admin', user) && (
              <FileInput
                label={i18n.t('button.addImages')}
                inputRef={photosInputRef}
                inputProps={{
                  accept: '.jpeg, .jpg, .png, .webp',
                  multiple: true,
                }}
                handleChangeFiles={handleChangePhotos}
              />
            )}
          </HideInput>
        </DashboardFormCustomColumn>
        {showToRoles('admin', user) && (
          <>
            <BorderLine space={'dashboard-form-card-around'} />
            <DashboardFooterWithResize columns={'max-content 1fr'}>
              <Button
                size={'medium'}
                data-color={'primary'}
                onClick={() => photosInputRef.current.click()}
                justify={'left'}
              >
                {i18n.t('button.addImages')}
              </Button>
              <Button
                size={'medium'}
                data-color={'primary'}
                onClick={handleDownloadImagesClick}
                justify={'left'}
                disabled={!oldPhotos.length}
              >
                {i18n.t('button.downloadImages')}
              </Button>
              <Button
                size={'medium'}
                data-color={'white'}
                onClick={handleCancelClick}
                disabled={!filteredStates.photosLength && !filteredStates.isDeletePhotos}
              >
                {i18n.t('button.cancel')}
              </Button>
              {showToRoles('client', user) && <span />}
              <Button
                size={'medium'}
                data-color={'primary'}
                onClick={handleSubmitClick}
                disabled={!filteredStates.photosLength && !filteredStates.isDeletePhotos}
              >
                {i18n.t('button.save')}
              </Button>
            </DashboardFooterWithResize>
          </>
        )}
      </DashboardFormCard>
    </>
  );
};

export const ContainersViewDocuments = ({ container }: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const updateDocumentsMutation = useUpdateDocumentsMutation();
  const isSmallScreen = useIsSmallScreen();

  const [documents, setDocuments] = useState<any>([]);
  const documentsFiles = useRef<any>([]);
  const [oldDocuments, setOldDocuments] = useState<any>(container.documents || []);
  const deleteDocuments = useRef<any>([]);
  const documentsInputRef = useRef<any>();
  const [, setStates, filteredStates] = useStatesWithFilterCounter(
    {
      documentsLength: 0,
      isDeleteDocuments: false,
    },
    {},
    0,
    false,
  );

  const handleChangeDocuments = (newDocuments: any, files: any) => {
    const newDocumentsValues = [...documents, ...newDocuments];

    setDocuments(newDocumentsValues);
    documentsFiles.current = [...documentsFiles.current, ...files];
    setStates('documentsLength', newDocumentsValues.length);
    documentsInputRef.current.type = 'text';
    documentsInputRef.current.type = 'file';
  };

  const handleDownloadDocumentsClick = (i: number) => {
    const gallery = [...oldDocuments, ...documentsFiles.current];
    const galleryDownload = [...oldDocuments, ...documents];

    containersService.downloadDocument(
      galleryDownload[i].url || galleryDownload[i],
      gallery[i].name,
    );
  };

  const handleDeleteDocumentsClick = (i: number) => {
    if (i - oldDocuments.length >= 0) {
      i = i - oldDocuments.length;
      documentsFiles.current = documentsFiles.current.filter(
        (_: any, index: number) => index !== i,
      );

      setDocuments(documents.filter((_: any, index: number) => index !== i));
      setStates('documentsLength', documents.length - 1);
    } else {
      deleteDocuments.current = [
        ...deleteDocuments.current,
        { action: 'delete', name: oldDocuments[i].name },
      ];

      setOldDocuments(oldDocuments.filter((_: any, index: number) => index !== i));
      setStates('isDeleteDocuments', true);
    }
  };

  const handleCancelClick = () => {
    documentsFiles.current = [];
    deleteDocuments.current = [];
    setDocuments([]);
    setOldDocuments(container.documents || []);
    setStates('force', {
      documentsLength: 0,
      isDeleteDocuments: false,
    });
  };

  const handleSubmitClick = async () => {
    const data: any = {};

    if (documents.length > 10) {
      enqueueSnackbar(INPUT_ERRORS.NOT_VALID.MAX_UPLOAD_FILES, 'warning');
      return;
    }
    data.documents = getFilesChanges(documents, documentsFiles.current, deleteDocuments.current);

    if (!data.documents.length) return;
    if (!container.id) return;

    await updateDocumentsMutation.mutate(
      { id: container.id, documents: data.documents },
      {
        onSuccess: (res) => {
          if (res) {
            enqueueSnackbar(MESSAGES.CONTAINERS.SUCCESS.FILES_CHANGE, 'success');
            container.documents = [
              ...oldDocuments,
              ...documents.map((file: any, i: string) => ({
                description: '',
                name: res[i].name || documentsFiles.current[i].name,
                url: res[i].url || file,
              })),
            ];
            documentsFiles.current = [];
            setOldDocuments(container.documents);
            setDocuments([]);
            setStates('force', {
              documentsLength: 0,
              isDeleteDocuments: false,
            });
          } else enqueueSnackbar(MESSAGES.ERROR_BOUNDARY.DEFAULT, 'error');
        },
      },
    );
  };

  return (
    <>
      {isSmallScreen && (
        <Typography variant={'textM'} weight={'semibold'} color={'--gray-90'}>
          {i18n.t('pages.containerView.documents')}
        </Typography>
      )}
      <DashboardFormCard
        paperSize={isSmallScreen ? 'none' : 'full-size'}
        rows={'max-content max-content 1fr'}
      >
        {!isSmallScreen && (
          <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
            {i18n.t('pages.containerView.documents')}
          </Typography>
        )}
        {!isSmallScreen && !oldDocuments.length && !documents.length && (
          <BorderLine space={'dashboard-form-card'} />
        )}
        <DashboardFormCustomColumn
          columns={'1fr'}
          alignContent={!oldDocuments.length && !documents.length ? 'center' : 'initial'}
          withoutGap={!isSmallScreen}
        >
          {!oldDocuments.length && !documents.length && (
            <EmptyFilesMessage variant={'textM'} align={'center'} gridColumn={'1 / 4'}>
              {i18n.t('pages.containerView.noDocuments')}
            </EmptyFilesMessage>
          )}
          <HideInput>
            {showToRoles('admin', user) && (
              <FileInput
                label={i18n.t('button.addDocuments')}
                inputRef={documentsInputRef}
                inputProps={{
                  accept: '*',
                  multiple: true,
                }}
                handleChangeFiles={handleChangeDocuments}
                withoutCompress
              />
            )}
          </HideInput>
          {[...oldDocuments, ...documentsFiles.current].map((file: any, i: any) => {
            let handleDeleteFileClick = showToRoles('admin', user)
              ? () => handleDeleteDocumentsClick(i)
              : null;

            if (
              oldDocuments.length > i &&
              !showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_MANAGER'], user)
            ) {
              handleDeleteFileClick = null;
            }

            return (
              <FileRow
                key={i}
                file={file}
                variant={isSmallScreen ? 'input' : 'row'}
                handleDownloadClick={() => handleDownloadDocumentsClick(i)}
                handleDeleteFileClick={handleDeleteFileClick}
                withoutBorder={isSmallScreen}
              />
            );
          })}
        </DashboardFormCustomColumn>
        {showToRoles('admin', user) && (
          <>
            <BorderLine space={'dashboard-form-card-around'} />
            <DashboardFooterWithResize columns={'1fr'}>
              <Button
                size={'medium'}
                data-color={'primary'}
                onClick={() => documentsInputRef.current.click()}
                justify={'left'}
              >
                {i18n.t('button.addDocuments')}
              </Button>
              <Button
                size={'medium'}
                data-color={'white'}
                onClick={handleCancelClick}
                disabled={!filteredStates.documentsLength && !filteredStates.isDeleteDocuments}
              >
                {i18n.t('button.cancel')}
              </Button>
              <Button
                size={'medium'}
                data-color={'primary'}
                onClick={handleSubmitClick}
                disabled={!filteredStates.documentsLength && !filteredStates.isDeleteDocuments}
              >
                {i18n.t('button.save')}
              </Button>
            </DashboardFooterWithResize>
          </>
        )}
      </DashboardFormCard>
    </>
  );
};

export const ContainersViewHistories = ({
  histories = [],
}: {
  histories: ContainerGetOneQuery['histories'];
}) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const isSmallScreen = useIsSmallScreen();

  return (
    <ContainersViewHistoriesWrapper>
      <DashboardHeader>
        <Typography variant={isSmallScreen ? 'textL' : 'textXL'} weight={'bold'}>
          {i18n.t('pages.containerView.histories')}
        </Typography>
      </DashboardHeader>
      <SimpleTable
        data={{
          data: histories,
          totalCount: histories.length,
        }}
        tableRowProps={(_, i) => ({
          key: i,
          onClick: () => {},
        })}
        columns={[
          {
            headText: () => i18n.t('label.action'),
            body: (history) => (
              <TableCell component={'th'} align={'left'}>
                {containerHistoriesComment[history.action].name}
              </TableCell>
            ),
          },
          {
            headText: () => i18n.t('label.createdAt'),
            bodyText: (history) => momentTextFormat(history.createdAt, user),
          },
          // {
          //   headText: () => i18n.t('label.comment'),
          //   body: (history) => (
          //     <TableCellComment comment={containerHistoriesComment[history.action].comment || ''} />
          //   ),
          //   column: 10,
          // },
          {
            headText: () => i18n.t('label.userName'),
            bodyText: (history) => `${history.user.firstName} ${history.user.lastName}`,
          },
        ]}
      />
    </ContainersViewHistoriesWrapper>
  );
};
